import type { JSX } from 'react'
import type { CarouselWithPrice } from '~/widgets/DynamicModule/ui/cards/banner/index.h'

import { useUnit } from 'effector-react'
import { cloneElement, memo } from 'react'
import { useInView } from 'react-intersection-observer'
import { bannerSlide, model as routerModel } from '~/core/router'
import { BannerRowContainerWithTrailerPortal } from '~/widgets/Banners/BannerRowContainerWithTrailerPortal'
import { useBannerCarouselCards } from '~/widgets/DynamicModule/ui/cards/banner/useBannerCarouselCards'

type ModuleBannerCarouselProps = {
  carousels: CarouselWithPrice[]
  isLoading?: boolean
  dataTestId: string
  detailsCategoryName: string
  detailsTitle: string
}

type ModuleBannerCarouselRowProps = {
  carousel: CarouselWithPrice
  isLoading: boolean
  dataTestId: string
  detailsCategoryName: string
  detailsTitle: string
}

const ModuleBannerCarouselRow = memo(ModuleBannerCarouselRow_)
ModuleBannerCarouselRow_.displayName = 'ModuleBannerCarouselRow'

function ModuleBannerCarouselRow_({
  carousel,
  isLoading,
  dataTestId,
  detailsCategoryName,
  detailsTitle,
}: ModuleBannerCarouselRowProps): JSX.Element | null {
  const content = carousel.bannerBundles
  const autoScrollEnabled = carousel.autoScrollEnabled
  const autoScrollIn = carousel.autoScrollIn
  const bannerId = carousel.bannerCarouselId

  const locationKey = useUnit(routerModel.$key)

  const initialSlide =
    locationKey && bannerId != null
      ? bannerSlide.getBannerSlide(locationKey, String(bannerId))
      : undefined

  const {
    chooseBannerCard,
    handleBannerSlideChanged,
    isBannerAutoScrollEnabled,
    isAnyPaymentPopupOpened,
    goToNextSlideTrigger,
  } = useBannerCarouselCards(
    {
      categoryId: 0,
      categoryName: detailsCategoryName,
      detailsTitle,
    },
    {
      content,
      bannerId,
      autoScrollIn,
      autoScrollEnabled,
    }
  )

  if (!isLoading && !content?.length) {
    return null
  }

  return (
    <BannerRowContainerWithTrailerPortal
      infiniteLoop={true}
      loaded={!isLoading}
      childrenLength={content?.length ?? 0}
      autoScrollEnabled={isBannerAutoScrollEnabled}
      isPopupOpen={isAnyPaymentPopupOpened}
      autoScrollIn={autoScrollIn}
      onSlideChanged={handleBannerSlideChanged}
      initialSlide={initialSlide}
      goToNextSlideTrigger={goToNextSlideTrigger}
      dataTestIdBannersCarousel={dataTestId}
    >
      <>
        {content?.map((item) =>
          cloneElement(chooseBannerCard(item), {
            key: String(item.bannerBundleId),
          })
        )}
      </>
    </BannerRowContainerWithTrailerPortal>
  )
}

export const ModuleBannerCarousel = memo(ModuleBannerCarousel_)
ModuleBannerCarousel_.displayName = 'ModuleBannerCarousel'

function ModuleBannerCarousel_({
  carousels,
  isLoading = false,
  dataTestId,
  detailsCategoryName,
  detailsTitle,
}: ModuleBannerCarouselProps): JSX.Element {
  const { ref: setRef, inView } = useInView()
  const hasAnyContent = carousels.some(
    ({ bannerBundles }) => bannerBundles?.length
  )

  if (!inView && !hasAnyContent) {
    return <div ref={setRef} />
  }

  if (!isLoading && !hasAnyContent) {
    return <div ref={setRef} />
  }

  return (
    <div ref={setRef}>
      {carousels.map((carousel, index) => (
        <ModuleBannerCarouselRow
          key={String(carousel.bannerCarouselId ?? index)}
          carousel={carousel}
          isLoading={isLoading}
          dataTestId={dataTestId}
          detailsCategoryName={detailsCategoryName}
          detailsTitle={detailsTitle}
        />
      ))}
    </div>
  )
}
